import React, { FC, useEffect, useState } from "react";
import {
  AckErrorMessage,
  AckLoader,
  AckTable,
  AckTableRowFormatter,
  AckTableRowProps,
  convertToDisplayValue,
  formatDecimal,
  ModelSpec,
  useFetch,
} from "ackbox-weblib-react";
import {
  CurrencyType,
  Empty,
  formatDecoratedCurrency,
  formatDecoratedPercentage,
  RESOURCES_API_PATH,
  useEffectOnce,
} from "./Constants";
import { Progress, useProgress } from "./Progress";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSearchParams } from "react-router-dom";

/** Sums a number array. Defaults to 0 if the array is empty. */
const sumOf = (array: number[]) => {
  if (array.length === 0) return 0;
  return array.reduce((prev, next) => prev + next);
};

/** Computes the style that the risk level should be shown. */
const formatDecoratedRiskPercentage = (amount: number) => {
  let modifier = "has-text-dark";
  if (amount > 0 && amount <= 40) {
    modifier = "has-text-info";
  } else if (amount > 40 && amount <= 60) {
    modifier = "has-text-primary";
  } else if (amount > 60 && amount <= 90) {
    modifier = "has-text-warning";
  } else if (amount > 90) {
    modifier = "has-text-danger";
  }
  return (<span className={modifier}>{formatDecimal(amount)}%</span>);
};

const HoldingsSummary = () => {
  const [data, setData] = useState({} as any);
  const ackFetch = useFetch(RESOURCES_API_PATH);

  const { errors, isLoading } = useEffectOnce(async () => {
    // Maps to HoldingsBreakdown DTO.
    const data = await ackFetch.get("securities/holdings/");
    setData(data);
  });

  const OverallSecuritySummary: FC = () => {
    // Maps to SecurityHolding DTO.
    const spec: ModelSpec[] = [
      { key: "security", label: "Security", name: "security", type: "object" },
      { key: "riskLevel", label: "Risk Percentage", name: "riskPercentage", type: "object" },
      { key: "unitQuantity", label: "Remaining Units", name: "unitQuantity", type: "decimal" },
      { key: "unitAcb", label: "ACB/Unit (CAD)", name: "unitAcb", type: "currency" },
      { key: "totalAcb", label: "ACB (CAD)", name: "totalAcb", type: "currency" },
      { key: "unitPrice", label: "Unit Price (CAD)", name: "unitPrice", type: "currency" },
      { key: "totalHoldings", label: "Total (CAD)", name: "totalHoldings", type: "currency" },
      { key: "capitalGainsPercent", label: "Capital Gains (%)", name: "capitalGainsPercent", type: "decimal" },
      { key: "capitalGains", label: "Capital Gains (CAD)", name: "capitalGains", type: "currency" },
    ];
    const securities = data.securities as any[];
    const rows: AckTableRowProps[] = securities.map((summary: any) => ({ spec: spec, payload: summary }));
    const totalValue = sumOf(securities.map((summary: any) => summary.totalHoldings));
    const totalGains = sumOf(securities.map((summary: any) => summary.capitalGains));
    const footer = {
      "Total Value | Total Capital Gains": (
        <strong>{formatDecoratedCurrency(totalValue)} | {formatDecoratedCurrency(totalGains)}</strong>),
    };
    const formatters: AckTableRowFormatter = (spec: ModelSpec, payload: any, _: number): any => {
      if (spec.name === "security") {
        return payload["security"].ticker;
      }
      if (spec.name === "riskPercentage") {
        return formatDecoratedRiskPercentage(payload["security"].riskLevel * 100);
      }
      if (spec.name === "capitalGains") {
        return (<strong>{formatDecoratedCurrency(payload[spec.name])}</strong>);
      }
      if (spec.name === "capitalGainsPercent") {
        return (<strong>{formatDecoratedPercentage(payload[spec.name])}</strong>);
      }
      return convertToDisplayValue(spec, payload);
    };
    return (
      <>
        <h1 className="title is-4">Security Holdings</h1>
        <AckTable rows={rows} footer={footer} formatters={formatters}/>
      </>
    );
  };

  const OverallAccountSummary: FC = () => {
    // Maps to AccountHolding DTO.
    const spec: ModelSpec[] = [
      { key: "account", label: "Account", name: "account", type: "object" },
      { key: "riskLevel", label: "Risk Percentage", name: "riskPercentage", type: "object" },
      { key: "totalHoldings", label: "Total (CAD)", name: "totalHoldings", type: "currency" },
    ];
    const accounts = data.accounts as any[];
    const rows: AckTableRowProps[] = accounts.map((summary: any) => ({ spec: spec, payload: summary }));
    const totalValue = sumOf(accounts.map((summary: any) => summary.totalHoldings));
    const footer = { "Total Value": (<strong>{formatDecoratedCurrency(totalValue)}</strong>) };
    const formatters: AckTableRowFormatter = (spec: ModelSpec, payload: any, _: number): any => {
      if (spec.name === "account") {
        return payload["account"].name;
      }
      if (spec.name === "riskPercentage") {
        return formatDecoratedRiskPercentage(payload["account"].riskLevel * 100);
      }
      return convertToDisplayValue(spec, payload);
    };
    return (
      <>
        <h1 className="title is-4">Account Holdings</h1>
        <AckTable rows={rows} footer={footer} formatters={formatters}/>
      </>
    );
  };

  const OverallSummary: FC = () => {
    return (
      <>
        {data.securities.length > 0 && <OverallSecuritySummary/>}
        {data.accounts.length > 0 && <OverallAccountSummary/>}
      </>
    );
  };


  const AssetAllocation: FC = () => {
    const holdingsEquityRatio = data.assetAllocation.holdingsEquityRatio;
    const acbEquityRatio = data.assetAllocation.acbEquityRatio;
    return (
      <>
        <h1 className="title is-4">Asset Allocation</h1>
        <p>Equity Percentage (ACB): <strong>{formatDecoratedRiskPercentage(acbEquityRatio * 100)}</strong></p>
        <p>Equity Percentage (Current): <strong>{formatDecoratedRiskPercentage(holdingsEquityRatio * 100)}</strong></p>
        <br/>
      </>
    );
  };

  return (
    <>
      <AckErrorMessage errors={errors}/>
      <AckLoader isLoading={isLoading}/>
      {!isLoading && data && <AssetAllocation/>}
      {!isLoading && data && <OverallSummary/>}
      {!isLoading && !data && <Empty/>}
    </>
  );
};

const SecurityAcbDetails: FC<{ currency: string, summary: any }> = (props) => {
  // Maps to TradeAcb DTO.
  const currency = props.currency;
  const spec: ModelSpec[] = [
    { key: "createdAt", label: "Created At", name: "createdAt", type: "date" },
    { key: "tradeType", label: "Trade Type", name: "tradeType", type: "string" },
    { key: "unitQuantityTrade", label: "Trade Units", name: "unitQuantityTrade", type: "decimal" },
    { key: "tradeCommission", label: `Trade Commission (${currency})`, name: "tradeCommission", type: "currency" },
    { key: "tradeTotal", label: `Trade Total (${currency})`, name: "tradeTotal", type: "currency" },
    { key: "unitQuantityTotal", label: "Remaining Units", name: "unitQuantityTotal", type: "decimal" },
    { key: "changeAcb", label: `ACB Change (${currency})`, name: "changeAcb", type: "currency" },
    { key: "unitAcb", label: `ACB/Unit (${currency})`, name: "unitAcb", type: "currency" },
    { key: "totalAcb", label: `ACB Total (${currency})`, name: "totalAcb", type: "currency" },
    {
      key: "tradeCapitalGains",
      label: `Trade Capital Gains (${currency})`,
      name: "tradeCapitalGains",
      type: "currency",
    },
    { key: "capitalGains", label: `Capital Gains (${currency})`, name: "capitalGains", type: "currency" },
  ];
  const rows: AckTableRowProps[] = props.summary.trades.map((summary: any) => ({ spec: spec, payload: summary }));
  const footer = { "Total": (<strong>{formatDecoratedCurrency(props.summary.capitalGains)}</strong>) };
  const formatters: AckTableRowFormatter = (spec: ModelSpec, payload: any, _: number): any => {
    if (spec.name === "tradeCapitalGains") {
      if (Object.keys(payload.tags).length > 0) {
        // Include information about tags.
        const gainsValue = formatDecoratedCurrency(payload[spec.name]);
        const tags = Object.values(payload.tags).map((tag: any) => tag.value).join("\n");
        const TagWarning = () => {
          return (
            <span className="icon has-text-warning pl-1">
                <span data-tooltip={tags}>
                  <FontAwesomeIcon icon="exclamation-triangle" size="xs" fixedWidth={true}/>
                </span>
              </span>
          );
        };
        return (<strong>{gainsValue}<TagWarning/></strong>);
      }
      return (<strong>{formatDecoratedCurrency(payload[spec.name])}</strong>);
    }
    if (spec.name === "capitalGains") {
      return (<strong>{formatDecoratedCurrency(payload[spec.name])}</strong>);
    }
    return convertToDisplayValue(spec, payload);
  };
  return (<AckTable rows={rows} footer={footer} formatters={formatters}/>);
};

const AcbBySecuritySummary = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({} as Map<string, any[]>);
  const [securities, setSecurities] = useState([] as string[]);
  const [selectedSecurityTicker, setSelectedSecurityTicker] = useState(searchParams.get("tick") as string | undefined);
  const [selectedCurrency, setSelectedCurrency] = useState(CurrencyType.CAD as string | undefined);
  const ackFetch = useFetch(RESOURCES_API_PATH);

  const { errors, isLoading } = useEffectOnce(async () => await onLoad(), [selectedCurrency]);

  const onLoad = async () => {
    // Maps to SecurityAcbBreakdown DTO.
    const values = await ackFetch.get(`securities/acb/security/?currency=${selectedCurrency}`);
    const data = new Map<string, any[]>(values.map((value: any) => [`${value.security.ticker}`, value]));
    const availableSecurities = values.map((value: any) => `${value.security.ticker}`);
    setData(data);
    setSecurities(availableSecurities);
    if (!selectedSecurityTicker && availableSecurities.length > 0) {
      setSelectedSecurityTicker(availableSecurities[0]);
    }
  };

  useEffect(() => {
    if (selectedSecurityTicker) {
      setSearchParams({ "tick": selectedSecurityTicker });
    }
  }, [selectedSecurityTicker]);

  const CurrencySelector = () => {
    const onChange = async (value: any) => setSelectedCurrency(value);
    const optionLabels = Object.keys(CurrencyType).map((o, index) => (
      <option key={"option-" + index} value={o}>{o}</option>));
    return (
      <div className="field is-horizontal">
        <div className="field-label field-align-left is-normal">
          <label className="label is-left">Currency</label>
        </div>
        <div className="field-body">
          <div className="field">
            <div className="control">
              <div className="select">
                <select value={selectedCurrency} onChange={e => onChange(e.target.value)}>{optionLabels}</select>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SecuritySelector = () => {
    const onChange = (value: any) => setSelectedSecurityTicker(value);
    const optionLabels = securities.map((o, index) => (<option key={"option-" + index} value={o}>{o}</option>));
    const className = isLoading ? "select is-fullwidth is-loading" : "select is-fullwidth";
    return (
      <>
        <div className="field mb-6">
          <div className="control">
            <div className={className}>
              <select value={selectedSecurityTicker} onChange={e => onChange(e.target.value)}>{optionLabels}</select>
            </div>
          </div>
        </div>
      </>
    );
  };

  const SecurityDetails: FC<{ securityTicker: string, breakdown: any }> = (props) => {
    return (
      <>
        <h1 className="title is-4">{props.securityTicker} - {props.breakdown.security.name}</h1>
        <CurrencySelector/>
        {props.breakdown.summaries.map((value: any, index: number) => {
          return (
            <div key={index} className="section">
              <h2 className="subtitle is-6">{value.year}</h2>
              <SecurityAcbDetails currency={props.breakdown.currencyType} summary={value}/>
            </div>
          );
        })}
      </>
    );
  };
  return (
    <>
      <AckErrorMessage errors={errors}/>
      <AckLoader isLoading={isLoading}/>
      {!isLoading && securities.length > 0 && <SecuritySelector/>}
      {!isLoading && securities.length === 0 && <Empty/>}
      {!isLoading && selectedSecurityTicker &&
        <SecurityDetails securityTicker={selectedSecurityTicker} breakdown={data.get(selectedSecurityTicker)!}/>}
    </>
  );
};

const AcbByYearSummary = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({} as Map<string, any[]>);
  const [years, setYears] = useState([] as string[]);
  const [selectedYear, setSelectedYear] = useState(searchParams.get("y") as string | undefined);
  const [selectedCurrency, setSelectedCurrency] = useState(CurrencyType.CAD as string | undefined);
  const ackFetch = useFetch(RESOURCES_API_PATH);

  const { errors, isLoading } = useEffectOnce(async () => {
    // Maps to YearlyAcbBreakdown DTO.
    const values = await ackFetch.get(`securities/acb/yearly/?currency=${selectedCurrency}`);
    const data = new Map<string, any[]>(values.map((value: any) => [`${value.year}`, value]));
    const availableYears = values.map((value: any) => `${value.year}`);
    setData(data);
    setYears(availableYears);
    if (!selectedYear && availableYears.length > 0) {
      setSelectedYear(availableYears[0]);
    }
  }, [selectedCurrency]);

  useEffect(() => {
    if (selectedYear) {
      setSearchParams({ "y": selectedYear });
    }
  }, [selectedYear]);

  const CurrencySelector = () => {
    const onChange = async (value: any) => setSelectedCurrency(value);
    const optionLabels = Object.keys(CurrencyType).map((o, index) => (
      <option key={"option-" + index} value={o}>{o}</option>));
    return (
      <div className="field is-horizontal">
        <div className="field-label field-align-left is-normal">
          <label className="label is-left">Currency</label>
        </div>
        <div className="field-body">
          <div className="field">
            <div className="control">
              <div className="select">
                <select value={selectedCurrency} onChange={e => onChange(e.target.value)}>{optionLabels}</select>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const YearSelector = () => {
    const onChange = (value: any) => setSelectedYear(value);
    const optionLabels = years.map((o, index) => (<option key={"option-" + index} value={o}>{o}</option>));
    const className = isLoading ? "select is-fullwidth is-loading" : "select is-fullwidth";
    return (
      <div className="field mb-6">
        <div className="control">
          <div className={className}>
            <select value={selectedYear} onChange={e => onChange(e.target.value)}>{optionLabels}</select>
          </div>
        </div>
      </div>
    );
  };

  const YearSummary: FC<{ year: string, breakdown: any }> = (props) => {
    const currency = props.breakdown.currencyType;
    // Maps to YearAcb DTO.
    const spec: ModelSpec[] = [
      { key: "security", label: "Security", name: "security", type: "object" },
      { key: "unitQuantity", label: "Remaining Units", name: "unitQuantity", type: "decimal" },
      { key: "unitQuantitySold", label: "Units Sold", name: "unitQuantitySold", type: "decimal" },
      { key: "unitAcb", label: `ACB/Unit (${currency})`, name: "unitAcb", type: "currency" },
      { key: "totalAcb", label: `ACB (${currency})`, name: "totalAcb", type: "currency" },
      { key: "capitalGains", label: `Capital Gains (${currency})`, name: "capitalGains", type: "currency" },
    ];
    const rows: AckTableRowProps[] = props.breakdown.summaries.map((summary: any) => ({
      spec: spec,
      payload: summary,
    }));
    const formatters: AckTableRowFormatter = (spec: ModelSpec, payload: any, _: number): any => {
      if (spec.name === "security") {
        return payload[spec.name].ticker;
      }
      if (spec.name === "capitalGains") {
        return (<strong>{formatDecoratedCurrency(payload[spec.name])}</strong>);
      }
      return convertToDisplayValue(spec, payload);
    };
    return (
      <>
        <h2 className="subtitle is-5 mt-5">Summary</h2>
        <AckTable rows={rows} formatters={formatters}/>
      </>
    );
  };

  const YearDetails: FC<{ year: string, breakdown: any }> = (props) => {
    return (
      <>
        <h2 className="subtitle is-5">Details</h2>
        {props.breakdown.summaries.map((value: any, index: number) => {
          return (
            <div key={index} className="section">
              <p className="subtitle is-6">{value.security.ticker}</p>
              <SecurityAcbDetails currency={props.breakdown.currencyType} summary={value}/>
            </div>
          );
        })}
      </>
    );
  };
  return (
    <>
      <AckErrorMessage errors={errors}/>
      <AckLoader isLoading={isLoading}/>
      {!isLoading && years.length > 0 && <YearSelector/>}
      {!isLoading && years.length === 0 && <Empty/>}
      {!isLoading && selectedYear && <h1 className="title is-4">ACB - {selectedYear}</h1>}
      {!isLoading && years.length > 0 && <CurrencySelector/>}
      {!isLoading && selectedYear && <YearSummary year={selectedYear} breakdown={data.get(selectedYear)!}/>}
      {!isLoading && years.length > 0 && <hr/>}
      {!isLoading && selectedYear && <YearDetails year={selectedYear} breakdown={data.get(selectedYear)!}/>}
    </>
  );
};

export const SecuritiesAcbBySecuritySummary = () => {
  const progress = useProgress();
  return (
    <div className="section">
      <h1 className="title is-spaced">Adjusted Cost Base</h1>
      {!progress.isComplete && <Progress {...progress}/>}
      {progress.isComplete && <AcbBySecuritySummary/>}
    </div>
  );
};

export const SecuritiesAcbByYearSummary = () => {
  const progress = useProgress();
  return (
    <div className="section">
      <h1 className="title is-spaced">Adjusted Cost Base</h1>
      {!progress.isComplete && <Progress {...progress}/>}
      {progress.isComplete && <AcbByYearSummary/>}
    </div>
  );
};

export const SecuritiesHoldingsSummary = () => {
  const progress = useProgress();
  return (
    <div className="section">
      <h1 className="title is-spaced">Holdings Summary</h1>
      {!progress.isComplete && <Progress {...progress}/>}
      {progress.isComplete && <HoldingsSummary/>}
    </div>
  );
};
